import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { interval, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './authentication/auth.service';
import { SharedAuthenticationService } from './authentication/shared/shared-authentication.service';
import { AppSettingsService } from './services/common/appsettings.service';
import { ConstantService, ThemeEnum } from './services/common/constant.service';
import { UserSessionService } from './services/identity';
import { ThemeModelV2, ThemeService } from './services/organization';
import { ThemingService } from './services/common/theming.service';

import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { HeaderComponent } from './shared/header/header.component';
import { BreadCrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { RowDetailComponent } from './shared/row-detail/row-detail.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [SidebarComponent, HeaderComponent, BreadCrumbComponent, RouterOutlet, RowDetailComponent, TranslatePipe]
})
export class AppComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translate = inject(TranslateService);
  authService = inject(AuthService);
  private title = inject(Title);
  private sharedService = inject(SharedAuthenticationService);
  private constantService = inject(ConstantService);
  appSettings = inject(AppSettingsService);
  private cookieService = inject(CookieService);
  private userSessionService = inject(UserSessionService);
  private idle = inject(Idle);
  private themingService = inject(ThemingService);


  showRouter: boolean = false;
  showMenus: Menus  = Menus.Full;
  loadingLang: boolean = true;
  themeModel: ThemeModelV2 = {};
  logoUrl: string;

  exactRoutesNoMenus: string[] = [
    '/', '/login', '/signin', '/auth-error', '/auth/register'
  ]

  startsWithRoutesNoMenus: string[] = [
    '/auth/logout', '/auth-callback', '/auth/register-welcome',
    '/cloud/azure/custom-redirection',
    '/organization/licence/update',
  ]

  startsWithRoutesMinimalMenus: string[] = [
    '/auth/change-password', '/verify-user', '/enforce-push', '/enforce-fido', '/awaiting-enrollment', '/credential-reset/enrollment', '/enforce-enrollment', '/styled-message' ]

  private activeTimer: Subscription;

  constructor() {
    const router = this.router;


    // Removing Sidebar, Navbar, Footer for Documentation, Error and Auth pages
    router.events.forEach((event: any) => {


      if (event instanceof NavigationStart) {

        if (this.exactRoutesNoMenus.indexOf(event['url']) != -1 || this.startsWithRoutesNoMenus.some(x => event['url'].startsWith(x))) {
          this.showMenus = Menus.None;

        } else if (this.startsWithRoutesMinimalMenus.some(x => event['url'].startsWith(x))) {
          this.showMenus = Menus.Minimal;
        }
        else {
          const data = sessionStorage.getItem('dataconf') ;
          if (data!= '' && data != null && data != undefined) {
            return this.router?.navigate(JSON.parse(data));
          } else {
            this.showMenus = Menus.Full;

          }
        }


        if (window.matchMedia('(max-width: 991px)').matches) {
          document.querySelector('body')!.classList.remove('az-sidebar-show');
        }
      }
    });


  }
  ngOnDestroy(): void {
    this.activeTimer.unsubscribe();
  }

  async ngOnInit() {
    this.logoUrl = this.constantService.getImageFromServer(ThemeEnum.companyLogo) ?? ThemeEnum.companyLogoDefault;

    this.configureIdle();
    let user = await this.authService.getUser(false);
    if (user && window.location.pathname.indexOf('/auth/logout') == -1) {      
      this.authService.getUserPostLoginStatus(true);
    }

    // handle admin toggling for reload behavior
    if (this.cookieService.check('loadStatus')) {
      this.appSettings.settings.adminMenu = true;
      this.cookieService.delete('loadStatus');
    }
    window.addEventListener('beforeunload', () => {
      if (this.appSettings.settings.adminMenu) {
        const expires = new Date();
        expires.setSeconds(expires.getSeconds() + 5);
        this.cookieService.set('loadStatus', 'true', expires);
      }
    });
    // end handle admin toggling for reload behavior

    await this.getLocalSettings();
    this.title.setTitle(this.constantService.showOrgName);
    await this.themingService.getTheme();
    this.showRouter = true;
    this.checkUri();
    ;
    this.activeTimer = interval(60 * 1000).subscribe(() => {
      if (!this.idle.isIdling()) {
        this.updateActive();
      }
    })

  }

  logout(): void {
    this.sharedService.delayedSignout();
  }

  checkUri(): void {
    if (environment.production) {
      let publicUrl = environment.publicUrl;
      if (publicUrl.startsWith('.')) {
        publicUrl = publicUrl.substring(1);
      }
      if (window.location.host.indexOf(publicUrl) == -1) {
        const orgName = this.constantService.getSubdomain();
        window.location.hostname = `${orgName}.${publicUrl}`;
      }
    }
  }

  private async getLocalSettings() {

    await this.authService.loadLanguages();

    if (window.location.pathname == '/') {
      this.authService.redirectUser();
    }
    // navbar backdrop for mobile only
    const navbarBackdrop = document.createElement('div');
    navbarBackdrop.classList.add('az-navbar-backdrop');
    document.querySelector('body')!.appendChild(navbarBackdrop);
    navbarBackdrop.addEventListener('click', () => {
      document.querySelector('body')!.classList.toggle('az-sidebar-show');
    });

    this.loadingLang = false;
  }

  private configureIdle() {
    this.idle.setIdle(5 * 60);
    this.idle.setTimeout(8 * 60 * 60);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.watch();
    this.idle.onIdleStart.subscribe(() => {

    });

    this.idle.onIdleEnd.subscribe(() => {
      this.updateActive();
    });

    this.idle.onTimeout.subscribe(() => {
      this.authService.signOut();
    });

  }

  private updateActive() {
    const orgId = this.authService.user?.profile.organizationId ?? this.authService.user?.profile.OrganizationId;
    if (this.authService.sessionId && orgId) {
      this.userSessionService.apiUserSessionUpdateLastActiveOrganizationIdUserIdSessionIdPut(orgId, Number(this.authService.id), this.authService.sessionId).subscribe(data => {
      });
    }
  }

  
}
enum Menus {
  Full = 'Full',
  Minimal = 'Minimal',
  None = 'None'
}
